import { ref, onUnmounted, type Ref, onMounted } from 'vue'

export interface IntersectionObserverOptions {
  root?: Element | Document | null,
  rootMargin?: string,
  threshold?: number | number[]
}

/**
 * Vue composable for using Intersection Observer API
 * @param target The element to observe (Element, Ref<Element>, or a function returning Element)
 * @param options Intersection observer options
 * @param callback Optional callback function to run when intersection changes
 * @returns Object containing isIntersecting state and utilities
 */
export function useIntersectionObserver (
  target: Ref<Element | null | undefined> | Element | (() => Element | null | undefined),
  options: IntersectionObserverOptions = {},
  callback?: (entry: IntersectionObserverEntry) => void
) {
  const isIntersecting = ref(false)
  const entry = ref<IntersectionObserverEntry | null>(null)

  let observer: IntersectionObserver | null = null

  const stopObserving = () => {
    if (observer) {
      observer.disconnect()
      observer = null
    }
  }

  const observe = () => {
    stopObserving()

    observer = new IntersectionObserver((entries) => {
      const firstEntry = entries[0]
      if (!firstEntry) {
        return
      }
      isIntersecting.value = firstEntry.isIntersecting
      entry.value = firstEntry

      if (callback) {
        callback(firstEntry)
      }
    }, options)

    let el: Element | null | undefined = null

    if (target instanceof Element) {
      el = target
    } else if (typeof target === 'function') {
      el = target()
    } else {
      el = target.value
    }

    if (el) {
      observer.observe(el)
    }
  }

  // Start observing
  onMounted(observe)

  // Clean up observer when component is unmounted
  onUnmounted(stopObserving)

  return {
    isIntersecting,
    entry,
    observe,
    stopObserving
  }
}
